/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { JSX } from 'preact';
import type { ForwardedRef } from 'preact/compat';
import type { TestIdProps } from '#hooks/UNSAFE_useTestId';
import type { DimensionProps } from '#utils/UNSAFE_interpolations/dimensions';
import type { TableProps, TableViewHandle } from './Table.types';

import { forwardRef } from 'preact/compat';
import { classNames } from '#utils/UNSAFE_classNames';
import { dimensionInterpolations } from '#utils/UNSAFE_interpolations/dimensions';
import { mergeInterpolations } from '#utils/UNSAFE_mergeInterpolations';
import { Table } from '#PRIVATE_Table';
import { tableViewStyles } from './themes/TableViewStyles.css';

/**
 * Props specific to non-virtualized TableView
 */
type NonVirtualizedTableProps<K, D> = {
  /**
   * The data that is used to render each item in TableView.  If the value is undefined, the
   * TableView will show nothing if hasMore is false, or loading indicator if hasMore is true.
   */
  data?: D[];
  /**
   * A callback function to extract the key which is used to identify the item.
   */
  getRowKey: (data: D) => K;
  /**
   * Whether there is more data available to load.  By default this returns false.
   */
  hasMore?: boolean;
  /**
   * An optional callback function which is invoked when the user scrolls to the end of the table.
   */
  onLoadMore?: () => void;
};

/**
 * Props for the TableView Component
 */
export type Props<K, D, C extends string> = TableProps<K, D, C> &
  NonVirtualizedTableProps<K, D> &
  DimensionProps &
  TestIdProps;

const mergedInterpolations = mergeInterpolations<DimensionProps>([
  ...Object.values(dimensionInterpolations)
]);

/**
 * Component that renders data in rows of columns.
 * This component is non-virtualized and renders all rows provided in the data prop.
 */
export const TableView = forwardRef(
  <K extends string | number, D, C extends string>(
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      getAccessibleRowHeaders,
      children,
      columns,
      columnOrder,
      columnWidths,
      contextMenuConfig,
      viewportConfig,
      layout = 'contents',
      data,
      getRowKey,
      hasMore = false,
      onLoadMore,
      currentCellOverride,
      onPersistCurrentCell,
      currentRowVariant,
      gridlines = { horizontal: 'visible', vertical: 'hidden' },
      selectionMode = { row: 'none', column: 'none' },
      selected,
      onSelectionChange,
      onRowAction,
      sortCriterion = [],
      onSortCriterionChange,
      horizontalScrollPositionOverride,
      verticalScrollPositionOverride,
      columnResizingIndicator,
      onColumnResizing,
      onColumnResize,
      noDataRenderer,
      scrollerRef,
      testId,
      ...dimensions
    }: Props<K, D, C>,
    ref?: ForwardedRef<TableViewHandle<K, C>>
  ) => {
    const { ...mergedStyles } = mergedInterpolations(dimensions);
    const isExternalScroller = viewportConfig != null;
    const classArray = [tableViewStyles.base];
    classArray.push(
      isExternalScroller ? tableViewStyles.externalScroller : tableViewStyles.internalScroller
    );
    const tableViewClasses = classNames(classArray);

    return (
      <div class={tableViewClasses} style={mergedStyles}>
        <Table<K, D, C>
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          getAccessibleRowHeaders={getAccessibleRowHeaders}
          children={children}
          columns={columns}
          columnOrder={columnOrder}
          columnWidths={columnWidths}
          contextMenuConfig={contextMenuConfig}
          viewportConfig={viewportConfig}
          layout={layout}
          data={data}
          getRowKey={getRowKey}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          currentCellOverride={currentCellOverride}
          onPersistCurrentCell={onPersistCurrentCell}
          currentRowVariant={currentRowVariant}
          gridlines={gridlines}
          selectionMode={selectionMode}
          selected={selected}
          onSelectionChange={onSelectionChange}
          onRowAction={onRowAction}
          horizontalScrollPositionOverride={horizontalScrollPositionOverride}
          verticalScrollPositionOverride={verticalScrollPositionOverride}
          sortCriterion={sortCriterion}
          onSortCriterionChange={onSortCriterionChange}
          columnResizingIndicator={columnResizingIndicator}
          onColumnResizing={onColumnResizing}
          onColumnResize={onColumnResize}
          noDataRenderer={noDataRenderer}
          scrollerRef={scrollerRef}
          testId={testId}
          ref={ref}
        />
      </div>
    );
  }
) as <K extends string | number, D, C extends string>(
  p: Props<K, D, C> & { ref?: ForwardedRef<TableViewHandle<K, C>> }
) => JSX.Element;
